import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import logo from '../../../assets/images/SVG/eduoneLogo.svg';
import ContactAdminModal from '../../common/ContactAdminModal/ContactAdminModal';
import { SuccessModal } from '../../common';
import authService from '../../../infrastructure/services/authService';

const SetPassword = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [matchError, setMatchError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [isSuccessOpen, setIsSuccessOpen] = useState(false);
    const isResetComplete = useRef(false);

    const resetTarget = sessionStorage.getItem('resetTarget');
    const resetOtp = sessionStorage.getItem('resetOtp');
    const resetUserId = sessionStorage.getItem('resetUserId');

    // Redirect if resetTarget or resetOtp is missing
    useEffect(() => {
        if (isResetComplete.current) return;
        if (!resetTarget || !resetOtp) {
            navigate('/forgot-password/email', { replace: true });
        }
    }, [resetTarget, resetOtp, navigate]);

    // Prevent back navigation by pushing current state to history
    useEffect(() => {
        if (!resetTarget || !resetOtp) return;

        window.history.pushState(null, document.title, window.location.href);

        const handlePopState = (e) => {
            window.history.pushState(null, document.title, window.location.href);
            setMatchError('Please set your new password to complete the reset process.');
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [resetTarget, resetOtp]);

    const handleCancelReset = () => {
        sessionStorage.removeItem('resetTarget');
        sessionStorage.removeItem('resetType');
        sessionStorage.removeItem('resetUserId');
        sessionStorage.removeItem('resetOtp');
        navigate('/', { replace: true });
    };

    if (!resetTarget || !resetOtp) {
        return null;
    }

    // Password strength rules logic
    const rules = {
        length: password.length >= 6,
        match: password.length > 0 && password === confirmPassword
    };

    const isAllRulesMet = rules.length && rules.match;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!password || !confirmPassword) {
            setMatchError('Both password fields are required');
            return;
        }

        if (!isAllRulesMet) {
            setMatchError('Please satisfy all password strength criteria');
            return;
        }

        if (password !== confirmPassword) {
            setMatchError("Password doesn't match");
            return;
        }

        if (!resetTarget || !resetOtp) {
            setMatchError('Session expired or invalid reset request. Please restart the forgot password process.');
            return;
        }

        setMatchError('');
        setLoading(true);
        try {
            await authService.forgotPasswordComplete(resetTarget, resetOtp, password, confirmPassword, resetUserId);
            isResetComplete.current = true;
            setIsSuccessOpen(true);
        } catch (err) {
            setMatchError(err?.message || 'Failed to complete password reset. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessDone = () => {
        setIsSuccessOpen(false);
        // Clear reset target and navigate back to login
        sessionStorage.removeItem('resetTarget');
        sessionStorage.removeItem('resetType');
        sessionStorage.removeItem('resetOtp');
        sessionStorage.removeItem('resetUserId');
        navigate('/');
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: '480px',
                my: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                fontFamily: "'Poppins', sans-serif"
            }}
        >
            {/* Logo */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mb: { xs: 4, md: 5 }
                }}
            >
                <Box
                    component="img"
                    src={logo}
                    alt="EduOne Logo"
                    sx={{
                        height: '45px',
                        width: 'auto'
                    }}
                />
            </Box>

            {/* Header Text */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                    variant="h2"
                    sx={{
                        fontSize: { xs: '26px', sm: '28px' },
                        fontWeight: 700,
                        color: '#231F20',
                        mb: 1.5,
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Set New Password
                </Typography>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#9B9B9B',
                        lineHeight: 1.6,
                        maxWidth: '400px',
                        mx: 'auto',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    You successfully verified ! Now you can enter new password.
                </Typography>
            </Box>

            {/* Form */}
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3
                }}
            >
                {/* Password field */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: matchError ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="6" width="20" height="12" rx="3" ry="3"></rect>
                                <circle cx="7" cy="12" r="1.5" fill="#94a3b8"></circle>
                                <circle cx="12" cy="12" r="1.5" fill="#94a3b8"></circle>
                                <circle cx="17" cy="12" r="1.5" fill="#94a3b8"></circle>
                            </svg>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        <Box
                            component="input"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                if (matchError) setMatchError('');
                            }}
                            sx={{
                                flex: 1,
                                padding: '16px',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '15px',
                                color: '#334155',
                                outline: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                width: '100%'
                            }}
                        />
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            sx={{
                                px: 2,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#94a3b8',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: '#64748b'
                                }
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {showPassword ? (
                                    <>
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </>
                                ) : (
                                    <>
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </>
                                )}
                            </svg>
                        </Box>
                    </Box>
                </Box>

                {/* Confirm Password field */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box
                        sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            border: '1.5px solid',
                            borderColor: matchError ? '#e74c3c' : '#cbd5e1',
                            borderRadius: '16px',
                            backgroundColor: '#ffffff',
                            transition: 'all 0.25s ease',
                            '&:focus-within': {
                                borderColor: '#ff502d',
                                boxShadow: '0 0 0 3px rgba(255, 80, 45, 0.1)'
                            }
                        }}
                    >
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="2" y="6" width="20" height="12" rx="3" ry="3"></rect>
                                <circle cx="7" cy="12" r="1.5" fill="#94a3b8"></circle>
                                <circle cx="12" cy="12" r="1.5" fill="#94a3b8"></circle>
                                <circle cx="17" cy="12" r="1.5" fill="#94a3b8"></circle>
                            </svg>
                        </Box>
                        <Box sx={{ width: '1.5px', height: '28px', backgroundColor: '#cbd5e1', alignSelf: 'center' }} />
                        <Box
                            component="input"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Re-Enter Password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                if (matchError) setMatchError('');
                            }}
                            sx={{
                                flex: 1,
                                padding: '16px',
                                border: 'none',
                                background: 'transparent',
                                fontSize: '15px',
                                color: '#334155',
                                outline: 'none',
                                fontFamily: "'Poppins', sans-serif",
                                width: '100%'
                            }}
                        />
                        <Box
                            component="button"
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            sx={{
                                px: 2,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                color: '#94a3b8',
                                transition: 'color 0.2s',
                                '&:hover': {
                                    color: '#64748b'
                                }
                            }}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                {showConfirmPassword ? (
                                    <>
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                        <circle cx="12" cy="12" r="3" />
                                    </>
                                ) : (
                                    <>
                                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                        <line x1="1" y1="1" x2="23" y2="23" />
                                    </>
                                )}
                            </svg>
                        </Box>
                    </Box>
                </Box>

                {/* Error feedback */}
                {matchError && (
                    <Typography
                        component="span"
                        sx={{
                            color: '#e74c3c',
                            fontSize: '14px',
                            mt: '-8px',
                            fontFamily: "'Poppins', sans-serif"
                        }}
                    >
                        {matchError}
                    </Typography>
                )}

                {/* Password strength checklist */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        mt: 1,
                        mb: 2
                    }}
                >
                    {/* Item 1 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {rules.length ? (
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="10" fill="#46CC6B" />
                                <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="9" stroke="#cbd5e1" strokeWidth="2" fill="none" />
                            </svg>
                        )}
                        <Typography sx={{ fontSize: '14px', color: rules.length ? '#475569' : '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>
                            6 characters minimum
                        </Typography>
                    </Box>

                    {/* Item 2 */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {rules.match ? (
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="10" fill="#46CC6B" />
                                <path d="M6 10L9 13L14 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="10" cy="10" r="9" stroke="#cbd5e1" strokeWidth="2" fill="none" />
                            </svg>
                        )}
                        <Typography sx={{ fontSize: '14px', color: rules.match ? '#475569' : '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>
                            Password and confirm password must match
                        </Typography>
                    </Box>
                </Box>

                {/* Submit Button */}
                <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                        width: '100%',
                        background: '#ff502d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '16px',
                        py: 2,
                        px: 3,
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        textTransform: 'none',
                        boxShadow: '0 8px 20px -6px rgba(255, 80, 45, 0.4)',
                        fontFamily: "'Poppins', sans-serif",
                        transition: 'all 0.25s ease',
                        '&:hover': {
                            background: '#e04324',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 24px -6px rgba(255, 80, 45, 0.5)'
                        },
                        '&.Mui-disabled': {
                            background: '#ff502d',
                            opacity: 0.6,
                            color: '#fff'
                        }
                    }}
                >
                    {loading ? 'Resetting Password...' : 'Submit'}
                </Button>

                {/* Cancel Link */}
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography
                        onClick={handleCancelReset}
                        sx={{
                            fontSize: '14px',
                            color: '#94a3b8',
                            fontFamily: "'Poppins', sans-serif",
                            cursor: 'pointer',
                            transition: 'color 0.2s',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: '#ff502d'
                            }
                        }}
                    >
                        Cancel reset and return to login
                    </Typography>
                </Box>
            </Box>

            {/* Support Link */}
            <Box sx={{ textAlign: 'center', mt: 5, mb: 1 }}>
                <Typography
                    sx={{
                        fontSize: '14px',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif"
                    }}
                >
                    Need help to reset password ?{' '}
                    <Box
                        component="span"
                        onClick={() => setIsContactOpen(true)}
                        sx={{
                            color: '#ff502d',
                            fontWeight: 600,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            '&:hover': {
                                color: '#e04324'
                            }
                        }}
                    >
                        Contact us
                    </Box>
                </Typography>
            </Box>

            {/* Reusable ContactAdminModal */}
            <ContactAdminModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />

            {/* Reusable SuccessModal */}
            <SuccessModal
                isOpen={isSuccessOpen}
                onClose={handleSuccessDone}
                showCloseButton={false}
                title="Password Changed Successfully!"
                description="Your password has been updated. You can now sign in using your new credentials."
                actionText="Login"
                onAction={handleSuccessDone}
            />
        </Box>
    );
};

export default SetPassword;
